import React from 'react';
import PrimaryButton, {
  PrimaryButtonProps,
} from '@ui/Button/legacy/PrimaryButton';
import { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import TextCopyButton, {
  TextCopyButtonProps,
} from '@ui/Button/legacy/TextCopyButton';
import IconTextButton, {
  IconTextButtonProps,
} from '@ui/Button/legacy/IconTextButton';
import TextButton from '@ui/Button/legacy/TextButton';
import DestructiveButton from '@ui/Button/legacy/DestructiveButton';
import OverlayButton from '@ui/Button/legacy/OverlayButton';
import CompactButton, {
  CompactButtonProps,
} from '@ui/Button/legacy/CompactButton';
import TextLinkButton, {
  TextLinkButtonProps,
} from '@ui/Button/legacy/TextLinkButton';
import IconButton, {
  IconButtonProps,
} from '@ui/Button/legacy/IconButton/IconButton';
import logger from 'util/logger';

/** `default` is a separate union member so `variant` discriminates cleanly (no duplicate literals). */
type PrimarySecondaryButtonProps = Omit<PrimaryButtonProps, 'variant'> & {
  variant: 'primary' | 'secondary';
};

type TextButtonProps = Omit<MuiButtonProps, 'variant'> & {
  variant: 'text';
};

/** IconButton `variant` ('chip') is unrelated to this wrapper's discriminant; omit so `text_copy` is unambiguous. */
type TextCopyButtonPropsWrapper = Omit<TextCopyButtonProps, 'variant'> & {
  variant: 'text_copy';
};

type DestructiveButtonPropsWrapper = Omit<PrimaryButtonProps, 'variant'> & {
  variant: 'destructive';
};

type OverlayButtonPropsWrapper = Omit<PrimaryButtonProps, 'variant'> & {
  variant: 'overlay';
};

type IconTextButtonPropsWrapper = IconTextButtonProps & {
  variant: 'icon_text';
  subvariant?: 'contained' | 'outlined';
};

/** Omit MUI Base `color` (HTML string) so it does not widen MUI `Button` `color` in other variants. */
type CompactButtonPropsWrapper = Omit<
  CompactButtonProps,
  'variant' | 'color'
> & {
  variant: 'compact';
  /** Maps to underlying `CompactButton` color variant (`primary` | `secondary`). */
  subvariant?: 'primary' | 'secondary';
};

type TextLinkButtonPropsWrapper = TextLinkButtonProps & {
  variant: 'text_link';
};

/** Omit legacy `variant` (`'chip'`) so it does not intersect to `never` with `'icon'`. */
type IconButtonPropsWrapper = Omit<IconButtonProps, 'variant'> & {
  variant: 'icon';
  /** Sub-style forwarded to `IconButton` as `variant` (e.g. chip styling). */
  subvariant?: 'chip';
};

export type { IconButtonProps };
export type ButtonIconProps = IconButtonPropsWrapper;

const Button = React.forwardRef<
  HTMLButtonElement,
  | PrimarySecondaryButtonProps
  | TextButtonProps
  | TextCopyButtonPropsWrapper
  | DestructiveButtonPropsWrapper
  | OverlayButtonPropsWrapper
  | IconTextButtonPropsWrapper
  | CompactButtonPropsWrapper
  | TextLinkButtonPropsWrapper
  | IconButtonPropsWrapper
>(function Button(props, ref) {
  switch (props.variant) {
    case 'primary': {
      const { variant, ...rest } = props as PrimarySecondaryButtonProps;
      return <PrimaryButton {...rest} ref={ref} data-variant="primary" />;
    }
    case 'secondary': {
      const { variant, ...rest } = props;
      return (
        <PrimaryButton
          {...rest}
          ref={ref}
          color="secondary"
          data-variant="secondary"
        />
      );
    }
    case 'text': {
      const { variant, ...rest } = props;
      return <TextButton {...rest} ref={ref} data-variant="text" />;
    }
    case 'text_copy': {
      const { variant, ...rest } = props;
      return <TextCopyButton {...rest} ref={ref} data-variant="text_copy" />;
    }
    case 'destructive': {
      const { variant, ...rest } = props;
      return (
        <DestructiveButton {...rest} ref={ref} data-variant="destructive" />
      );
    }
    case 'overlay': {
      const { variant, ...rest } = props;
      return <OverlayButton {...rest} ref={ref} data-variant="overlay" />;
    }
    case 'icon': {
      const { variant, subvariant, ...rest } = props;
      return (
        <IconButton
          {...rest}
          ref={ref}
          variant={subvariant}
          data-variant={'icon'}
        />
      );
    }
    case 'icon_text': {
      const { variant, subvariant, ...rest } = props;
      return <IconTextButton {...rest} ref={ref} data-variant="icon_text" />;
    }
    case 'compact': {
      const { variant, subvariant = 'primary', ...rest } = props;
      return (
        <CompactButton
          {...rest}
          ref={ref}
          variant={subvariant}
          data-variant="compact"
        />
      );
    }
    case 'text_link': {
      const { variant, ...rest } = props;
      return <TextLinkButton {...rest} data-variant="text_link" />;
    }
    default: {
      logger.warn('button variant not supported', {
        variant: (props as { variant?: string }).variant,
      });
      return null;
    }
  }
});

export default Button;
